import { Api, ApiUrls } from '@/Api';
import { putRequest } from '@/ApiHelpers';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { defineStore, storeToRefs } from 'pinia';

export interface UserStore {
  id?: string;
}
export interface UserCredentials {
  email?: string;
  password?: string;
}

export interface UserState {
  id?: string;
  name?: string;
  email?: string;
  isFree?: boolean;
  avatarUrl?: string;
  itemCount?: number;
  confirmetAt?: string;
  createdAt?: string;
  dropLimit?: number;
  softDropLimitExceeded?: boolean;
  organizationId?: string;
  alternativeSoftDropLimitExceeded?: boolean;
  role?: string; // User permission level (user, vip, admin)
  profile?: string; // Job role (engineering, sales, etc.)
  jwt?: string;
  pusher: {
    channel?: string;
    channelV5?: string;
  };
  organization?: {
    isUserAdmin?: boolean;
    isUserViewOnly?: boolean;
  };
  smartActionSetting?: string;
  smartActionSettingType?: string;
  preferences?: any;
}

export const useUserStore = defineStore('userStore', {
  state: (): UserState => ({
    id: undefined,
    name: undefined,
    email: undefined,
    isFree: undefined,
    avatarUrl: undefined,
    itemCount: undefined,
    confirmetAt: undefined,
    createdAt: undefined,
    dropLimit: undefined,
    softDropLimitExceeded: undefined,
    organizationId: undefined,
    alternativeSoftDropLimitExceeded: undefined,
    role: undefined,
    profile: undefined,
    jwt: undefined,
    pusher: {
      channel: undefined,
      channelV5: undefined,
    },
    organization: undefined,
    smartActionSetting: undefined,
    smartActionSettingType: undefined,
    preferences: {},
  }),
  actions: {
    initialize(userData: any) {
      this.$patch({
        id: userData.data.id,
        avatarUrl: userData.data.links?.avatarUrl,
        organizationId: userData?.data?.relationships.organizations.data[0].id,
        organization: userData?.currentOrganization,
        ...userData.data.attributes,
        preferences: userData.data.attributes.preferences || {},
      });

      return userData;
    },
    load() {
      return Api.user.currentUser().then(response => this.initialize({ data: response?.data?.user }));
    },
    signIn(credentials: UserCredentials) {
      if (!credentials.email || !credentials.password) {
        return Promise.reject('Missing user or password data');
      }

      const { id } = storeToRefs(useViewerItemStore());
      return Api.user.signIn(credentials.email, credentials.password, id?.value);
    },
    signUp(credentials: UserCredentials, signedUpFrom: string | null = null) {
      if (!credentials.email || !credentials.password) {
        return Promise.reject('Missing user or password data');
      }

      const { id } = storeToRefs(useViewerItemStore());
      return Api.user.signUp(credentials.email, credentials.password, id?.value, signedUpFrom);
    },
    async updateProfile(profileData: { name?: string; profile?: string }) {
      if (!this.id) {
        throw new Error('User ID is required for profile updates');
      }

      // Use API endpoint with user ID for profile updates
      const response = await putRequest(ApiUrls.user.update.replace('{{id}}', this.id), profileData);

      // Update the store state with the returned user data
      // If response is successful (status 200), update local state
      this.$patch({
        name: profileData.name || this.name,
        profile: profileData.profile || this.profile,
      });

      return response;
    },
  },
  getters: {
    isLoggedIn: (state: UserState) => !!state.id,
    itemCapacityPercent: (state: UserState) => {
      const itemCount = state?.itemCount;
      const limitDrops = state.dropLimit;

      if (!itemCount || !limitDrops) {
        return 0;
      }

      return Math.min(Math.round((itemCount / limitDrops) * 100), 100);
    },
    isOrgUserAdmin: (state: UserState) => state.organization?.isUserAdmin,
    isOrgUserViewOnly: (state: UserState) => state.organization?.isUserViewOnly,
  },
});
